import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Plus, ArrowLeft, Eye, Save, Trash2, ExternalLink, Pencil, Globe, EyeOff, FileText } from "lucide-react";
import { BrandLogo } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import { cmsList, cmsGet, cmsCreate, cmsUpdate, cmsDelete } from "@/lib/api";
import BlockEditor from "@/components/cms/BlockEditor";
import BlockRenderer from "@/components/cms/BlockRenderer";
import ShareBar from "@/components/cms/ShareBar";
import { toast } from "sonner";

const EMPTY = { title: "", slug: "", excerpt: "", cover_image: "", seo_title: "", meta_description: "", status: "draft", splink_short_url: "", blocks: [] };
const fieldCls = "w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-brand";

export default function CmsAdmin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Editors are locked to their own tenant; admins pick tenant via ?tenant=
  const tenant = user?.role === "editor" ? (user.tenant_id || "kerala") : (params.get("tenant") || "");
  const [pages, setPages] = useState(null);
  const [editing, setEditing] = useState(null); // null=list, {}=new/edit form

  useEffect(() => {
    if (user === null || (user && user.role !== "admin" && user.role !== "editor")) navigate("/login");
  }, [user, navigate]);

  const load = () => cmsList(tenant || undefined).then(setPages).catch(() => setPages([]));
  useEffect(() => { if (user) load(); }, [tenant, user]); // eslint-disable-line react-hooks/exhaustive-deps

  if (user === undefined || user === null || (user.role !== "admin" && user.role !== "editor")) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-brand" size={26} /></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between sticky top-0 z-30">
        <Link to="/"><BrandLogo size={34} /></Link>
        <div className="flex items-center gap-4">
          {tenant && <span className="text-xs font-bold uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1" data-testid="cms-tenant-badge">{tenant} tenant</span>}
          {user.role === "admin" && <Link to={tenant === "kerala" ? "/admin/kerala" : "/admin"} className="text-sm text-slate-500 hover:text-brand">Console</Link>}
          <span className="text-sm font-semibold text-slate-700">CMS</span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {!editing ? (
          <PageList pages={pages} onNew={() => setEditing({ ...EMPTY, tenant_id: tenant || null })} onEdit={async (p) => setEditing(await cmsGet(p.slug, tenant || undefined))} onDeleted={load} />
        ) : (
          <PageForm initial={editing} onBack={() => { setEditing(null); load(); }} />
        )}
      </main>
    </div>
  );
}

function PageList({ pages, onNew, onEdit, onDeleted }) {
  const del = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    await cmsDelete(p.id);
    toast.success("Page deleted");
    onDeleted();
  };
  return (
    <>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900 tracking-tight">Content Pages</h1>
          <p className="text-slate-500 mt-1">Create and manage rich media pages across your site.</p>
        </div>
        <button onClick={onNew} data-testid="cms-new" className="btn-primary rounded-lg px-5 py-2.5 flex items-center gap-2"><Plus size={16} /> New Page</button>
      </div>
      {!pages ? <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-brand" size={24} /></div> : (
        <div className="bg-white border border-slate-200 rounded-2xl card-shadow overflow-hidden">
          {pages.length === 0 && <p className="p-8 text-slate-400 text-sm">No pages yet. Create your first page.</p>}
          {pages.map((p) => (
            <div key={p.id} className="flex items-center justify-between px-6 py-4 border-b border-slate-50 last:border-0" data-testid="cms-page-row">
              <div className="flex items-center gap-3">
                <FileText size={18} className="text-slate-300" />
                <div>
                  <p className="font-semibold text-slate-800">{p.title}</p>
                  <p className="text-xs text-slate-400 font-mono">/page/{p.slug}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${p.status === "published" ? "text-green-700 bg-green-50" : "text-amber-700 bg-amber-50"}`}>{p.status}</span>
                <a href={`/page/${p.slug}`} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-brand"><ExternalLink size={16} /></a>
                <button onClick={() => onEdit(p)} className="text-slate-400 hover:text-brand" data-testid="cms-edit"><Pencil size={16} /></button>
                <button onClick={() => del(p)} className="text-slate-400 hover:text-red-500"><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function PageForm({ initial, onBack }) {
  const [form, setForm] = useState(initial);
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (statusOverride) => {
    if (!form.title.trim()) { toast.error("Add a page title first."); return null; }
    const payload = { ...form, status: statusOverride || form.status, slug: form.slug || form.title };
    setSaving(true);
    try {
      const saved = form.id ? await cmsUpdate(form.id, payload) : await cmsCreate(payload);
      setForm(saved);
      toast.success(statusOverride === "published" ? "Published!" : "Saved");
      return saved;
    } catch (e) {
      toast.error("Save failed. Admin login required.");
      return null;
    } finally { setSaving(false); }
  };

  const openLive = async () => {
    const saved = await save();
    if (saved) window.open(`/page/${saved.slug}`, "_blank");
  };

  const pageUrl = form.id ? `${window.location.origin}/page/${form.slug}` : "";

  return (
    <>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <button onClick={onBack} className="text-slate-500 hover:text-brand flex items-center gap-1.5 text-sm"><ArrowLeft size={15} /> All pages</button>
        <div className="flex items-center gap-2">
          <button onClick={() => setPreview(!preview)} data-testid="cms-preview-toggle" className={`rounded-lg px-4 py-2.5 text-sm flex items-center gap-2 ${preview ? "btn-primary" : "btn-outline"}`}>
            {preview ? <Pencil size={15} /> : <Eye size={15} />} {preview ? "Edit" : "Preview"}
          </button>
          <button onClick={openLive} className="btn-outline rounded-lg px-4 py-2.5 text-sm flex items-center gap-2"><ExternalLink size={15} /> Open</button>
          <button onClick={() => save()} disabled={saving} data-testid="cms-save" className="btn-outline rounded-lg px-4 py-2.5 text-sm flex items-center gap-2">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save Draft
          </button>
          {form.status === "published" ? (
            <button onClick={() => save("draft")} className="btn-outline rounded-lg px-4 py-2.5 text-sm flex items-center gap-2"><EyeOff size={15} /> Unpublish</button>
          ) : (
            <button onClick={() => save("published")} disabled={saving} data-testid="cms-publish" className="btn-primary rounded-lg px-4 py-2.5 text-sm flex items-center gap-2"><Globe size={15} /> Publish</button>
          )}
        </div>
      </div>

      {preview ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-10">
          <div className="mb-4 inline-block text-xs font-bold uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1">Preview — not published until you click Publish</div>
          {form.cover_image && <img src={form.cover_image} alt="" className="w-full h-72 object-cover rounded-xl mb-6" />}
          <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-slate-900 tracking-tight">{form.title || "Untitled"}</h1>
          {form.excerpt && <p className="text-xl text-slate-500 mt-3">{form.excerpt}</p>}
          <div className="mt-8"><BlockRenderer blocks={form.blocks} /></div>
          <div className="mt-10"><ShareBar url={pageUrl || "https://websitetech.pro"} title={form.title} shortUrl={form.splink_short_url} /></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-6">
            <input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="cms-title" placeholder="Page title" className="w-full text-3xl font-heading font-extrabold text-slate-900 outline-none placeholder:text-slate-300 mb-4" />
            <input value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} placeholder="Short excerpt / subtitle" className="w-full text-lg text-slate-500 outline-none placeholder:text-slate-300 mb-6 border-b border-slate-100 pb-4" />
            <BlockEditor blocks={form.blocks} onChange={(blocks) => set("blocks", blocks)} />
          </div>
          <aside className="space-y-5">
            <Panel title="Page Settings">
              <Label>URL slug</Label>
              <input value={form.slug} onChange={(e) => set("slug", e.target.value)} placeholder="auto from title" className={fieldCls} />
              <Label>Cover image URL</Label>
              <input value={form.cover_image} onChange={(e) => set("cover_image", e.target.value)} className={fieldCls} />
            </Panel>
            <Panel title="SEO">
              <Label>SEO title</Label>
              <input value={form.seo_title} onChange={(e) => set("seo_title", e.target.value)} className={fieldCls} />
              <Label>Meta description</Label>
              <textarea value={form.meta_description} onChange={(e) => set("meta_description", e.target.value)} className={`${fieldCls} min-h-[70px]`} />
            </Panel>
            <Panel title="SPLink.pro">
              <Label>Short URL</Label>
              <input value={form.splink_short_url} onChange={(e) => set("splink_short_url", e.target.value)} placeholder="paste SPLink.pro short URL" className={fieldCls} />
              <p className="text-[11px] text-slate-400 mt-2">Add your SPLink.pro short URL — it powers the share bar link and QR code. Full SPLink.pro API wires in when you provide your code.</p>
            </Panel>
          </aside>
        </div>
      )}
    </>
  );
}

function Panel({ title, children }) {
  return <div className="bg-white border border-slate-200 rounded-2xl p-5"><p className="overline mb-3">{title}</p><div className="space-y-1">{children}</div></div>;
}
function Label({ children }) {
  return <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mt-3 mb-1">{children}</p>;
}
